import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Code, ShoppingCart, Zap, Bot, Box, TrendingUp,
  ArrowRight, Loader2
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

// Icon mapping
const iconMap = {
  'code': Code,
  'shopping-cart': ShoppingCart,
  'zap': Zap,
  'bot': Bot,
  'box': Box,
  'trending-up': TrendingUp
};

const Solutions = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (!error && data) {
        setServices(data);
      }
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const texts = {
    tr: {
      title: 'Çözümlerimiz',
      subtitle: 'İşletmenizi büyütecek dijital çözümler',
      description: 'Teknoloji ve yaratıcılığı birleştirerek markanızı geleceğe taşıyoruz. Her projede en yüksek kalite standartlarını uyguluyoruz.',
      learnMore: 'Detaylı Bilgi',
      getQuote: 'Teklif Al',
      noServices: 'Henüz hizmet eklenmemiş.'
    },
    en: {
      title: 'Our Solutions',
      subtitle: 'Digital solutions to grow your business',
      description: 'We combine technology and creativity to take your brand into the future. We apply the highest quality standards in every project.',
      learnMore: 'Learn More',
      getQuote: 'Get Quote',
      noServices: 'No services added yet.'
    }
  };

  const t = texts[language] || texts.tr;

  // Gradient colors for cards
  const gradients = [
    'from-blue-500 to-cyan-500',
    'from-purple-500 to-pink-500',
    'from-green-500 to-emerald-500',
    'from-orange-500 to-red-500',
    'from-indigo-500 to-purple-500',
    'from-pink-500 to-rose-500'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <SEO
          title={language === 'tr' ? 'Dijital Çözümler & Hizmetler' : 'Digital Solutions & Services'}
          description={language === 'tr' ? 'Cplus Media LLC; E-ticaret, Web Geliştirme, AI Otomasyon ve Performans Pazarlama çözümlerimizle tanışın.' : 'Discover Cplus Media LLC E-commerce, Web Development, AI Automation and Performance Marketing solutions.'}
          keywords="dijital çözümler, e-ticaret, web geliştirme, yapay zeka, otomasyon, performans pazarlama"
        />
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={language === 'tr' ? 'Dijital Çözümler & Hizmetler' : 'Digital Solutions & Services'}
        description={language === 'tr' ? 'E-ticaret, Web Geliştirme, AI Otomasyon ve Performans Pazarlama çözümlerimizle tanışın.' : 'Discover our E-commerce, Web Development, AI Automation and Performance Marketing solutions.'}
        keywords="dijital çözümler, e-ticaret, web geliştirme, yapay zeka, otomasyon, performans pazarlama"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto mb-4"
          >
            {t.subtitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 max-w-2xl mx-auto"
          >
            {t.description}
          </motion.p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {services.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400">{t.noServices}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const IconComponent = iconMap[service.icon] || Code;
              const gradient = gradients[idx % gradients.length];
              const title = language === 'tr' ? service.title_tr : service.title_en;
              const description = language === 'tr' ? service.description_tr : service.description_en;

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="group"
                >
                  <Link to={`/solutions/${service.slug}`}>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      {/* Icon */}
                      <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-3">
                        {description}
                      </p>

                      {/* Link */}
                      <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium group-hover:gap-3 transition-all">
                        <span>{t.learnMore}</span>
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'tr' ? 'Projenizi konuşalım' : "Let's discuss your project"}
            </h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              {language === 'tr'
                ? 'İhtiyaçlarınıza özel çözümler için bizimle iletişime geçin. Ücretsiz danışmanlık görüşmesi ayarlayalım.'
                : 'Contact us for solutions tailored to your needs. Let us schedule a free consultation meeting.'}
            </p>
            <Link
              to="/portal/new-quote"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              {t.getQuote}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Solutions;
